import { useParams } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";

import { useConnectorMergeTagScope } from "./ConnectorMergeTagScope";
import {
  buildTextComposerMergeTagCategories,
  buildTextComposerPickerCategories,
  syncConnectorMergeTagRegistry,
  type TextComposerMergeTagCategory,
} from "./mergeTagDefinitions";

import { useInstalledConnectors } from "@/api/connectors/index";
import { useReviewRatingsAvailable } from "@/hooks/useReviewRatingsAvailable";
import {
  collectCustomerMetadataTags,
  collectEventMergeTags,
  customerMetadataTagsToCategories,
  type MergeTagCategory,
} from "@/services/mergeTags";

const NO_CONNECTORS: never[] = [];

function useExtraConnectorMergeTagCategories(): MergeTagCategory[] {
  const { siteId } = useParams({ strict: false });
  const { connectorId, selectedEventId } = useConnectorMergeTagScope();
  // installedConnectorsQueryOptions sets enabled: !!siteId — empty siteId
  // does not fire a network request.
  const { data: installed = NO_CONNECTORS } = useInstalledConnectors(
    siteId ?? "",
  );

  return useMemo(() => {
    const connector = connectorId
      ? installed.find((item) => item.id === connectorId)
      : undefined;
    const tags = [
      ...collectCustomerMetadataTags(installed),
      ...collectEventMergeTags(connector?.events, selectedEventId),
    ];
    return customerMetadataTagsToCategories(tags);
  }, [connectorId, installed, selectedEventId]);
}

/**
 * Picker categories for Text Composer, including loyalty metadata tags from
 * installed connectors and payload tags for the selected connector event.
 */
export function useTextComposerMergeTagCategories(): TextComposerMergeTagCategory[] {
  const { siteId } = useParams({ strict: false });
  const extraCategories = useExtraConnectorMergeTagCategories();
  const reviewRatingsAvailable = useReviewRatingsAvailable(siteId);

  // Sync in an effect, not render: the sync notifies subscribers, and
  // notifying mid-render triggers a "setState while rendering" warning.
  useEffect(() => {
    syncConnectorMergeTagRegistry(extraCategories);
  }, [extraCategories]);

  return useMemo(
    () =>
      buildTextComposerPickerCategories(extraCategories, {
        reviewRatingsAvailable,
      }),
    [extraCategories, reviewRatingsAvailable],
  );
}

/**
 * Shared MergeTagCategory[] extras for InlineLexicalInput / Grapes hosts.
 * Also keeps the Text Composer connector registry in sync for chip lookup.
 */
export function useConnectorMetadataMergeTagCategories(): MergeTagCategory[] {
  const categories = useExtraConnectorMergeTagCategories();

  useEffect(() => {
    // Keep Lexical chip/auto-detect registry warm even when only Grapes /
    // InlineLexicalInput consume the categories (not MergeTagsSection).
    buildTextComposerMergeTagCategories(categories);
  }, [categories]);

  return categories;
}
